/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import {
  Grid,
  Typography,
  Paper,
  List,
  ListItem,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControlLabel,
  Switch,
  LinearProgress,
  Box
} from '@material-ui/core';
import clsx from 'clsx';
import OnlineIndicator from '../OnlineIndicator';
import { makeStyles } from '@material-ui/styles';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Skeleton } from '@material-ui/lab';
import uuid from 'react-uuid';
import UpdateIcon from '@material-ui/icons/Update';
import useDigitalDispatchLiveDispatchUpdates from 'src/hooks/useDigitalDispatchLiveDispatchUpdates';
import DigitalDispatchService from 'src/services/DigitalDispatchService';
import { useSnackbar } from 'notistack';
import { DIGITAL_DISPATCH_NETWORK_TOWBOOK } from 'src/constants/digitalProviderNetwork';
import { towBookDispatchPostApprovalUpdateParser } from 'src/utils/towBookDispatchUpdateParser';
import DispatchStatusList from './DispatchStatusList';
import DispatchLocationUpdatesMap from './DispatchLocationUpdatesMap';

const digitalDispatchService = new DigitalDispatchService();

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2)
  },
  title: {
    textAlign: 'center',
    marginBottom: theme.spacing(3)
  },
  mapSection: {
    height: '40rem',
  },
  dispatchUpdateSection: {
    display: 'flex',
    height: '40rem',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start'
  },
  dispatchUpdateTitle: {
    margin: theme.spacing(2),
    padding: theme.spacing(2),
    width: '100%',
    textAlign: 'center',
  },
  dispatchUpdateList: {
    width: '100%',
    overflowX: 'hidden'
  },
  dispatchUpdateListItem: {
    padding: theme.spacing(2),
    width: '100%',
    margin: theme.spacing(0, 1),
  },
  headingContainer: {
    display: 'flex',
    flexDirection: 'column'
  },
  boldText: {
    fontWeight: theme.typography.fontWeightBold
  },
  cardSection: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    padding: theme.spacing(3.5)
  },
  cardSectionHeader: {
    textAlign: 'center',
    width: '100%',
    marginBottom: theme.spacing(1)
  },
  card: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  cardHeader: {
    alignSelf: 'center'
  },
  onlineIndicatorSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1),
    flexWrap: 'wrap'
  },
  onlineIndicatorInnerSection: {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end'
  },
  onlineIndicatorText: {
    marginRight: theme.spacing(1)
  },
  onlineIndicatorSwitch: {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
  },
  onlineIndicatorOffline: {
    fill: 'red',
  },
  postApprovalSection: {
    display: 'flex',
    height: '40rem',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: theme.spacing(2),
  },
  postApprovalTitle: {
    marginBottom: theme.spacing(2)
  },
  providerPaymentLoadingContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%'
  },
  providerPaymentLoadingIndicator: {
    width: '100%'
  }
}));

const appInsightsEvent = 'DispatchQoreFE::PerformanceTracking::LiveDispatchUpdates::WebPubSubConnectionError';

function LiveDispatchUpdates({
  dispatch,
  dispatchDropOffLocation,
  dispatchPickUpLocation,
  dispatchHasPayment
}) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const dispatchId = dispatch?.dispatchId;
  const {
    closeConnection,
    isConnected,
    isFetchingClientAccessUri,
    startListeningForDigitalDispatchUpdates,
    startConnection,
    dispatchStatusUpdates,
    areDispatchStatusUpdatesLoading,
    dispatchLocationUpdates,
    areDispatchLocationUpdatesLoading,
    areDispatchPostApprovalUpdatesLoading,
    dispatchPostApprovalUpdates,
    setWebPubSubError,
    setDispatchPostApprovalUpdates
  } = useDigitalDispatchLiveDispatchUpdates(dispatchId, appInsightsEvent);

  const [webPubSubConnectionSwitchState, setWebPubSubConnectionSwitchState] = useState(isConnected);
  const [
    driverWaypointHistorySwitchState,
    setDriverWaypointHistorySwitchState
  ] = useState(false);

  const [showEventDialog, setShowEventDialog] = useState(false);
  const [selectedPostApprovalEvent, setSelectedPostApprovalEvent] = useState({});
  const [dispatchHasProviderPayment, setDispatchHasProviderPayment] = useState(false);
  const [isLoadingProviderPaymentStatus, setIsLoadingProviderPaymentStatus] = useState(false);

  async function fetchDispatchProviderPaymentStatus(towNetId, producerCode) {
    try {
      if (!producerCode) {
        return;
      }
      setIsLoadingProviderPaymentStatus(true);
      const response = await digitalDispatchService.doesDispatchHaveProviderPayment(towNetId, producerCode);
      setDispatchHasProviderPayment(response?.data?.hasPayment);
    } catch (e) {
      enqueueSnackbar('Error fetching provider payment status', {
        variant: 'error'
      });
    } finally {
      setIsLoadingProviderPaymentStatus(false);
    }
  }

  useEffect(() => {
    fetchDispatchProviderPaymentStatus(dispatch?.towNetId, dispatch?.companyIdentifier);
  }, [dispatch]);

  useEffect(() => {
    setWebPubSubConnectionSwitchState(isConnected);
  }, [isConnected]);

  useEffect(() => {
    async function toggleConnection() {
      try {
        if (webPubSubConnectionSwitchState) {
          await startConnection();
          await startListeningForDigitalDispatchUpdates();
        } else if (!webPubSubConnectionSwitchState && !isFetchingClientAccessUri) {
          await closeConnection();
        }
      } catch (error) {
        enqueueSnackbar(error?.message ?? 'Failed to refresh webpubsub client connection', {
          variant: 'error',
          preventDuplicate: true
        });
        setWebPubSubError(error);
      }
    }
    toggleConnection();
  }, [webPubSubConnectionSwitchState]);

  /**
   * Function used to update local post approval event state
   * when an agent responds. Updates the record with the agent
   * response value, updatedEta, remarks, notes and disables the submit button
   * @param {Object} updatedPostApprovalEvent
   * @returns {void}
   */
  const updateLocalPostApprovalEventState = (updatedPostApprovalEvent) => {
    if (!dispatchPostApprovalUpdates?.length) return;
    setDispatchPostApprovalUpdates((previousPostApprovalState) => previousPostApprovalState
      .map((currentPostApprovalEvent) => {
        const isTowBookUpdate = currentPostApprovalEvent.digitalProviderNetwork === DIGITAL_DISPATCH_NETWORK_TOWBOOK;

        if (currentPostApprovalEvent.id === updatedPostApprovalEvent.id) {
          return isTowBookUpdate ? {
            ...towBookDispatchPostApprovalUpdateParser(currentPostApprovalEvent),
            motorClubResponseRemarks: updatedPostApprovalEvent.motorclubResponseRemarks,
            motorClubResponseNotes: updatedPostApprovalEvent.motorclubResponseNotes,
            motorClubResponse: updatedPostApprovalEvent.motorclubResponse
          } : {
            ...currentPostApprovalEvent,
            motorClubResponseRemarks: updatedPostApprovalEvent.motorclubResponseRemarks,
            motorClubResponseNotes: updatedPostApprovalEvent.motorclubResponseNotes,
            motorClubResponse: updatedPostApprovalEvent.motorclubResponse
          };
        } if (isTowBookUpdate) {
          return towBookDispatchPostApprovalUpdateParser(currentPostApprovalEvent);
        }
        return currentPostApprovalEvent;
      }));
  };

  const handlePostApprovalEventResponse = async (values) => {
    try {
      await digitalDispatchService.sendPostApprovalEventResponse(
        values,
        dispatchHasProviderPayment,
        dispatch
      );
      enqueueSnackbar('Post Approval Event Response Sent', {
        variant: 'success',
        preventDuplicate: true
      });
    } catch (e) {
      enqueueSnackbar(e, {
        variant: 'error',
        preventDuplicate: true
      });
    } finally {
      setShowEventDialog(false);
    }
  };

  const handleDialogClose = () => {
    setShowEventDialog(false);
    setSelectedPostApprovalEvent({});
  };

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} event
   * @returns {void}
   */
  function onChangeWebPubSubSwitch(event) {
    setWebPubSubConnectionSwitchState(event.target.checked);
  }

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} event
   * @returns {void}
   */
  function onChangeDriverHistoryWaypointsSwitch(event) {
    setDriverWaypointHistorySwitchState(event.target.checked);
  }

  /**
   * @returns {React.ReactElement}
   */
  function dispatchStatusRecordsToListItemJsx() {
    if (areDispatchStatusUpdatesLoading) {
      return (
        Array(5).fill(1).map(() => (
          <ListItem
            key={uuid()}
            alignItems="flex-start"
            divider
            className={classes.dispatchUpdateListItem}
          >
            <Card className={classes.card} raised>
              <CardHeader
                title={(
                  <Skeleton width={350} height={50} />
                )}
                avatar={<UpdateIcon fontSize="large" />}
                className={classes.cardHeader}
              />
              <CardContent>
                <div className={classes.cardSection}>
                  <Skeleton width={450} height={200} />
                  <Divider />
                  <Skeleton width={450} height={200} />
                  <Divider />
                  <Skeleton width={450} height={200} />
                </div>
              </CardContent>
            </Card>
          </ListItem>
        ))
      );
    }
    return (
      <DispatchStatusList dispatchStatusUpdates={dispatchStatusUpdates} />
    );
  }

  return (
    <Grid className={classes.root} container spacing={2} direction="row" justifyContent="center" alignItems="center">
      <Grid item xs={12} className={classes.headingContainer}>
        <Typography variant="h3" className={classes.title}>
          Dispatch Status Updates
          {` Dispatch ID: ${dispatchId}`}
        </Typography>
        <div className={classes.onlineIndicatorSection}>
          <div className={classes.onlineIndicatorInnerSection}>
            <Typography variant="h5" className={classes.onlineIndicatorText}>
              {isConnected ? 'Connected:' : 'Disconnected:'}
            </Typography>
            <OnlineIndicator
              className={isConnected
                ? classes.onlineIndicatorSwitch
                : clsx(classes.onlineIndicatorSwitch, classes.onlineIndicatorOffline)}
              size="large"
              status={isConnected ? 'online' : 'offline'}
            />
          </div>
          <div>
            <FormControlLabel
              control={(
                <Switch
                  color="secondary"
                  checked={driverWaypointHistorySwitchState}
                  // eslint-disable-next-line react/jsx-no-bind
                  onChange={onChangeDriverHistoryWaypointsSwitch}
                  name="driverLocationHistory"
                  size="small"
                />
              )}
              label="Toggle Driver Location History"
            />
          </div>
          <FormControlLabel
            control={(
              <Switch
                color="secondary"
                checked={webPubSubConnectionSwitchState}
                // eslint-disable-next-line react/jsx-no-bind
                onChange={onChangeWebPubSubSwitch}
                name="onlineStatus"
                size="small"
              />
            )}
            label="Toggle Connection"
          />
        </div>
      </Grid>
      <Grid item xs={6} md={6}>
        <Paper elevation={3} className={classes.mapSection} component="section">
          <section>
            <DispatchLocationUpdatesMap
              dispatchDropOffLocation={dispatchDropOffLocation}
              dispatchPickUpLocation={dispatchPickUpLocation}
              dispatchLocationUpdates={dispatchLocationUpdates}
              driverWaypointHistorySwitchState={driverWaypointHistorySwitchState}
              isLoading={areDispatchLocationUpdatesLoading}
            />
          </section>
        </Paper>
      </Grid>
      <Grid item xs={6} md={6}>
        <Paper elevation={3} className={classes.dispatchUpdateSection} component="section">
          <Typography variant="h4" className={classes.dispatchUpdateTitle}>Dispatch Updates</Typography>
          <PerfectScrollbar style={{ width: '100%' }}>
            <List className={classes.dispatchUpdateList}>
              {dispatchStatusRecordsToListItemJsx()}
            </List>
          </PerfectScrollbar>
        </Paper>
      </Grid>
    </Grid>
  );
}

LiveDispatchUpdates.propTypes = {
  dispatch: PropTypes.shape({
    dispatchId: PropTypes.number,
    companyIdentifier: PropTypes.number,
    serviceKey: PropTypes.string,
    thirdPartyDispatchId: PropTypes.string,
    towNetId: PropTypes.number,
    twilioCallSid: PropTypes.string,
    specialInstructions: PropTypes.string,
    internalNotes: PropTypes.string,
    providerNotes: PropTypes.string,
    dateCreated: PropTypes.string,
    dateModified: PropTypes.string,
    clientPickUpTime: PropTypes.string,
    providerId: PropTypes.number,
    driverId: PropTypes.number,
    customer: PropTypes.shape({
      customerId: PropTypes.number,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      dateCreated: PropTypes.string,
      dateModified: PropTypes.string,
      email: PropTypes.string,
      preferredContactMethodId: PropTypes.number,
      emailOptIn: PropTypes.oneOf([0, 1]),
      mobileNotificationsOptIn: PropTypes.oneOf([0, 1]),
      smsOptIn: PropTypes.oneOf([0, 1]),
      phoneOptIn: PropTypes.oneOf([0, 1]),
      chatEmailOptIn: PropTypes.oneOf([0, 1]),
      chatMobileNotificationsOptIn: PropTypes.oneOf([0, 1]),
      chatSmsOptIn: PropTypes.oneOf([0, 1]),
      avatar: PropTypes.string,
      addresses: PropTypes.arrayOf(PropTypes.shape({
        addressId: PropTypes.number,
        fullAddress: PropTypes.string,
        googleLocationId: PropTypes.string,
        address: PropTypes.string,
        city: PropTypes.string,
        stateProvidence: PropTypes.string,
        postalCode: PropTypes.string,
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        addressTypeId: PropTypes.number
      })),
      phones: PropTypes.arrayOf(PropTypes.shape({
        telephoneNumberId: PropTypes.number,
        telephoneNumber: PropTypes.string,
        telephoneName: PropTypes.string,
        phoneTypeId: PropTypes.number,
        phoneType: PropTypes.string
      }))
    }),
    status: PropTypes.string,
    statusId: PropTypes.number,
    serviceName: PropTypes.string,
    serviceId: PropTypes.number,
    state: PropTypes.string,
    stateId: PropTypes.number,
    vehicleMake: PropTypes.string,
    vehicleModel: PropTypes.string,
    vehicleYear: PropTypes.number,
    vehicleVin: PropTypes.string,
    vehicleStateId: PropTypes.number,
    vehicleStateDesc: PropTypes.string,
    vehicleColor: PropTypes.string,
    vehicleLicensePlate: PropTypes.string,
    vehicleMileage: PropTypes.number,
    pickUpLocation: PropTypes.shape({
      addressId: PropTypes.number,
      fullAddress: PropTypes.string,
      googleLocationId: PropTypes.string,
      address: PropTypes.string,
      city: PropTypes.string,
      stateProvidence: PropTypes.string,
      postalCode: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
      addressTypeId: PropTypes.number,
      locationType: PropTypes.string,
      locationName: PropTypes.string
    }),
    dropOffLocation: PropTypes.shape({
      addressId: PropTypes.number,
      fullAddress: PropTypes.string,
      googleLocationId: PropTypes.string,
      address: PropTypes.string,
      city: PropTypes.string,
      stateProvidence: PropTypes.string,
      postalCode: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
      addressTypeId: PropTypes.number,
      locationType: PropTypes.string,
      locationName: PropTypes.string
    }),
  }),
  dispatchPickUpLocation: PropTypes.shape({
    addressId: PropTypes.number,
    fullAddress: PropTypes.string,
    googleLocationId: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    stateProvidence: PropTypes.string,
    postalCode: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    addressTypeId: PropTypes.number,
    locationName: PropTypes.string
  }),
  dispatchDropOffLocation: PropTypes.shape({
    addressId: PropTypes.number,
    fullAddress: PropTypes.string,
    googleLocationId: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    stateProvidence: PropTypes.string,
    postalCode: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    addressTypeId: PropTypes.number,
    locationName: PropTypes.string
  }),
  /**
   * Used to determine if the status of a cancelled dispatch
   * should be Cancelled or CancelledRR (refund requested) when
   * a dispatch is cancelled via the Post Approval Service
   * Provider cancel request
   */
  dispatchHasPayment: PropTypes.bool
};

export default LiveDispatchUpdates;
